import { FC, useEffect, useState } from 'react';
import { Formik, Form } from 'formik';
import { Avatar, Box, Modal, Tooltip, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import * as FlagIcons from 'country-flag-icons/react/1x1';
import { hasFlag } from 'country-flag-icons';

import CustomButton from '../CustomButton';
import CustomInput from '../CustomInput';
import { useAppDispatch } from '../../hooks/redux';
import { Contact, deleteContactFromList, updateEmergencyContactList } from '../../store/slices/customizeSlice';
import {
  type ContactMemberPreviewResponse,
  useContactMemberPreviewMutation,
  useCreateContactMutation,
  useDeleteContactMutation,
  useUpdateContactMutation,
} from '../../store/services/userContactsApi';

import { GlobalIcon } from '../../components/Icons';
import { getCountryFromPhoneNumber, validatePhoneNumber } from '../../utils/phoneValidation';
import { buildAvatarUrl, generateInitials } from '../../utils/avatarUtils';

import styles from './ContactModal.module.scss';

const EMAIL_REGEX = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

const PhoneNumberFlagIcon: FC<{ phone: string }> = ({ phone }) => {
  const iso = getCountryFromPhoneNumber(phone);

  if (!iso || !hasFlag(iso)) {
    return (
      <GlobalIcon
        size={20}
        color="#5E6A81"
      />
    );
  }

  const Flag = FlagIcons[iso as keyof typeof FlagIcons];
  if (!Flag) {
    return (
      <GlobalIcon
        size={20}
        color="#5E6A81"
      />
    );
  }

  return (
    <Box
      aria-hidden
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 20,
        height: 20,
        flexShrink: 0,
        borderRadius: '50%',
        overflow: 'hidden',
        boxShadow: '0 0 0 1px rgba(94, 106, 129, 0.18)',
      }}
    >
      <Flag
        title=""
        style={{ width: 20, height: 20, display: 'block' }}
      />
    </Box>
  );
};

function useMemberPreviewForEmail(email: string | undefined) {
  const [data, setData] = useState<ContactMemberPreviewResponse | null>(null);
  const [previewMember] = useContactMemberPreviewMutation();

  useEffect(() => {
    const trimmed = email?.trim() ?? '';
    if (!trimmed || !EMAIL_REGEX.test(trimmed)) {
      setData(null);
      return;
    }
    const id = window.setTimeout(() => {
      previewMember({ email: trimmed })
        .unwrap()
        .then(res => setData(res.found ? res : null))
        .catch(() => setData(null));
    }, 400);
    return () => window.clearTimeout(id);
  }, [email, previewMember]);

  return data;
}

type MemberPreviewTooltipProps = {
  preview: ContactMemberPreviewResponse;
  email: string;
};

const MemberPreviewTooltip: FC<MemberPreviewTooltipProps> = ({ preview, email }) => {
  const [imgFailed, setImgFailed] = useState(false);
  const avatarUrl = buildAvatarUrl(preview.profile_picture);
  const initials = generateInitials(preview.first_name, preview.last_name, email);
  const displayName = `${preview.first_name || ''} ${preview.last_name || ''}`.trim() || email;
  const showImage = Boolean(avatarUrl) && !imgFailed;

  useEffect(() => {
    setImgFailed(false);
  }, [preview.profile_picture]);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, py: 0.25, maxWidth: 280 }}>
      <Avatar
        src={showImage ? avatarUrl! : undefined}
        alt=""
        imgProps={{
          onError: () => setImgFailed(true),
        }}
        sx={{
          width: 36,
          height: 36,
          fontSize: 14,
          fontWeight: 600,
          bgcolor: '#e3f2fd',
          color: '#0098f0',
        }}
      >
        {!showImage ? initials : undefined}
      </Avatar>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
        <Typography
          variant="h11Semibold"
          sx={{ lineHeight: 1.3 }}
          noWrap
        >
          {displayName}
        </Typography>
        {preview.is_self ? (
          <Typography
            variant="h12"
            sx={{ color: 'text.secondary' }}
          >
            Your account
          </Typography>
        ) : null}
      </Box>
    </Box>
  );
};

type ContactModalEmailFieldProps = {
  value: string;
  error?: string;
  touchedEmail: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onBlur: (e: React.FocusEvent<HTMLInputElement>) => void;
  customClass?: string;
};

const ContactModalEmailField: FC<ContactModalEmailFieldProps> = ({
  value,
  error,
  touchedEmail,
  onChange,
  onBlur,
  customClass,
}) => {
  const memberPreview = useMemberPreviewForEmail(value);
  const showTip = Boolean(memberPreview?.found);

  return (
    <Tooltip
      title={
        showTip && memberPreview ? (
          <MemberPreviewTooltip
            preview={memberPreview}
            email={value}
          />
        ) : (
          ''
        )
      }
      disableHoverListener={!showTip}
      placement="top-start"
      slotProps={{
        popper: {
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, -8],
              },
            },
          ],
        },
      }}
      arrow
      enterDelay={200}
    >
      <Box
        component="span"
        sx={{ display: 'block', width: '100%' }}
      >
        <CustomInput
          inputLabel="Email"
          inputType="text"
          inputName="email"
          value={value}
          required
          error={touchedEmail ? error : undefined}
          customClass={customClass}
          onChange={onChange}
          onBlur={onBlur}
          placeholder="email.example@gmail.com"
        />
      </Box>
    </Tooltip>
  );
};

type FormikValues = {
  firstName?: string;
  lastName?: string;
  username?: string;
  relationship?: string;
  email?: string;
  phoneNumber?: string;
};

type ContactModalProps = {
  closeModal: () => void;
  targetId?: number | null;
  contacts?: Contact;
  type: string;
  onShowSuccess?: (message: string) => void;
};

const ContactModal: FC<ContactModalProps> = ({ closeModal, contacts, type, targetId, onShowSuccess }) => {
  const dispatch = useAppDispatch();
  const [updateContact] = useUpdateContactMutation();
  const [createContact] = useCreateContactMutation();
  const [deleteContact] = useDeleteContactMutation();

  const [resErrors, setResErrors] = useState<{ attr: string | null; code: string; detail: string }[] | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const initialValues = {
    firstName: contacts?.first_name || '',
    lastName: contacts?.last_name || '',
    username: contacts?.username || '',
    relationship: contacts?.relationship || '',
    email: contacts?.email || '',
    phoneNumber: contacts?.phone_number || '',
  };

  const handleSubmit = async (values: FormikValues, { setFieldError }: any) => {
    try {
      setResErrors(null);
      const username = values.username?.trim() ?? '';

      if (targetId) {
        await updateContact({
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          username,
          email: values.email,
          relationship: values.relationship,
          type,
          id: targetId,
        }).unwrap();

        dispatch(
          updateEmergencyContactList({
            first_name: values.firstName,
            last_name: values.lastName,
            phone_number: values.phoneNumber,
            username,
            email: values.email,
            relationship: values.relationship,
            type,
            id: targetId,
          })
        );
      } else {
        const data = await createContact({
          first_name: values.firstName,
          last_name: values.lastName,
          phone_number: values.phoneNumber,
          username,
          email: values.email,
          relationship: values.relationship,
          type,
        }).unwrap();

        if (data) {
          dispatch(
            updateEmergencyContactList({
              ...data,
              type,
            })
          );
        }
        onShowSuccess?.('Contact added successfully');
      }

      closeModal();
    } catch (error: any) {
      const apiErrors = error?.data?.errors ?? [];
      const generalErrors: typeof resErrors = [];

      const fieldMap: Record<string, keyof FormikValues> = {
        first_name: 'firstName',
        last_name: 'lastName',
        username: 'username',
        relationship: 'relationship',
        email: 'email',
        phone_number: 'phoneNumber',
      };

      apiErrors.forEach((e: any) => {
        const field = fieldMap[e.attr];

        if (field) {
          setFieldError(field, e.detail);
        } else {
          generalErrors.push(e);
        }
      });

      setResErrors(generalErrors.length ? generalErrors : null);
    }
  };

  const handleDeleteContact = async () => {
    if (!targetId) return;
    setIsDeleting(true);
    try {
      await deleteContact(targetId).unwrap();
      dispatch(deleteContactFromList({ id: targetId }));
      setDeleteConfirmOpen(false);
      onShowSuccess?.('Contact removed');
      closeModal();
    } catch (error) {
      const apiErrors = (error as any)?.data?.errors;
      if (apiErrors?.length) {
        setResErrors(apiErrors);
      } else {
        setResErrors([{ attr: null, code: 'unknown', detail: 'Something went wrong' }]);
      }
      console.error(error);
    } finally {
      setIsDeleting(false);
    }
  };

  const formikValidate = (values: FormikValues) => {
    const errors: FormikValues = {};
    if (!values.firstName) errors.firstName = 'First name is required';
    if (!values.lastName) errors.lastName = 'Last name is required';
    if (!values.relationship) errors.relationship = 'Relationship is required';
    if (!values.username?.trim()) errors.username = 'Username is required';
    if (!values.email) {
      errors.email = 'Email is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(values.email)) {
      errors.email = 'Invalid email address';
    }
    if (values.phoneNumber && !validatePhoneNumber(values.phoneNumber)) {
      errors.phoneNumber = 'Please enter a valid phone number (with country code, e.g. +1…)';
    }
    return errors;
  };

  return (
    <Box className={styles.accountInfo}>
      <Typography
        variant="h7Bold"
        sx={{ color: '#222B3A', fontSize: '28px' }}
      >
        {targetId ? `${type} contact` : `New ${type.toLowerCase()} contact`}
      </Typography>

      <Formik
        initialValues={initialValues}
        validate={formikValidate}
        onSubmit={handleSubmit}
      >
        {({ values, errors, touched, handleChange, handleBlur, isSubmitting, setFieldValue }) => {
          const handleFieldChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            setResErrors(null);
            handleChange(e);
          };

          return (
            <Form className={styles.accountInfoForm}>
              <Box className={styles.nameInputs}>
                <CustomInput
                  inputLabel="First Name"
                  inputType="text"
                  inputName="firstName"
                  value={values.firstName}
                  required
                  error={touched.firstName ? errors.firstName : undefined}
                  customClass={styles.customInput}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                />
                <CustomInput
                  inputLabel="Last Name"
                  inputType="text"
                  inputName="lastName"
                  value={values.lastName}
                  required
                  error={touched.lastName ? errors.lastName : undefined}
                  customClass={styles.customInput}
                  onChange={handleFieldChange}
                  onBlur={handleBlur}
                />
              </Box>

              <CustomInput
                inputType="text"
                inputName="username"
                inputLabel="Username"
                value={values.username}
                error={touched.username ? errors.username : undefined}
                customClass={styles.customInput}
                onChange={handleFieldChange}
                onBlur={handleBlur}
                required
              />

              <CustomInput
                inputLabel="Relationship"
                inputType="text"
                inputName="relationship"
                value={values.relationship}
                required
                error={touched.relationship ? errors.relationship : undefined}
                customClass={styles.customInput}
                onChange={handleFieldChange}
                onBlur={handleBlur}
              />

              <ContactModalEmailField
                value={values.email}
                error={errors.email}
                touchedEmail={Boolean(touched.email)}
                customClass={styles.customInput}
                onChange={handleFieldChange}
                onBlur={handleBlur}
              />

              <CustomInput
                inputLabel="Phone Number (optional)"
                inputName="phoneNumber"
                value={values.phoneNumber}
                error={touched.phoneNumber ? errors.phoneNumber : undefined}
                customClass={styles.customInput}
                startIcon={<PhoneNumberFlagIcon phone={values.phoneNumber} />}
                onChange={handleFieldChange}
                onBlur={handleBlur}
              />

              {resErrors?.map((error, index) => (
                <Typography
                  key={index}
                  variant="h12"
                  sx={{ color: '#C8102E', mt: 1 }}
                >
                  {error.detail}
                </Typography>
              ))}

              <Box className={styles.accountBtns}>
                {targetId && (
                  <CustomButton
                    btnType="button"
                    variant="outlined"
                    onClick={() => setDeleteConfirmOpen(true)}
                    disabled={isSubmitting}
                    customClass={styles.deleteButton}
                  >
                    Delete contact
                  </CustomButton>
                )}

                {!targetId && (
                  <CustomButton
                    btnType="button"
                    variant="outlined"
                    onClick={closeModal}
                    disabled={isSubmitting}
                  >
                    Cancel
                  </CustomButton>
                )}
                <CustomButton
                  btnType="submit"
                  customClass={styles.customButton}
                  disabled={isSubmitting}
                >
                  {targetId ? 'Save changes' : 'Save'}
                </CustomButton>
              </Box>
            </Form>
          );
        }}
      </Formik>

      <Modal
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        aria-labelledby="modal-title"
        aria-describedby="modal-description"
      >
        <Box
          sx={{
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            minWidth: 343,
            width: '90%',
            maxWidth: 400,
            bgcolor: 'background.paper',
            borderRadius: 3,
            p: { xs: 3, sm: 4 },
            overflowY: 'auto',
            boxShadow: 24,
          }}
        >
          <Box sx={{ position: 'absolute', top: 16, right: 20, cursor: 'pointer' }}>
            <CloseIcon
              sx={{ width: 30, height: 30 }}
              onClick={() => setDeleteConfirmOpen(false)}
            />
          </Box>

          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
            <Typography
              id="modal-title"
              variant="h5"
              sx={{ fontWeight: 'bold', textAlign: 'center' }}
            >
              Delete contact?
            </Typography>
            <Typography
              id="modal-description"
              variant="body1"
              sx={{ color: 'text.secondary', textAlign: 'center' }}
            >
              Are you sure you want to delete this contact?
            </Typography>

            <Box sx={{ mt: 3, width: '100%', display: 'flex', justifyContent: 'center', gap: 2 }}>
              <CustomButton
                variant="outlined"
                onClick={() => setDeleteConfirmOpen(false)}
              >
                Cancel
              </CustomButton>
              <CustomButton
                variant="contained"
                onClick={handleDeleteContact}
                customClass={styles.deleteButtonModal}
              >
                Delete
              </CustomButton>
            </Box>
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default ContactModal;
